import { Reveal, Stagger, StaggerItem, SectionNo, Hairline, MaskReveal } from "@/components/primitives";
import BigSectionNo from "@/components/BigSectionNo";
import { useScroll } from "@/lib/scroll";
import { images } from "@/config/site";

// Pricing lives on the enquiry — the three tiers here are the story of what
// each engagement is, never a number. The single CTA on every card opens
// the enquiry form.
const TIERS = [
  {
    key: "property-film",
    name: "Property Film",
    line: "One hero film for the property, and every cutdown a booking channel needs.",
    features: [
      "A 60-second hero film for the website and booking pages",
      "3 vertical cutdowns for Reels, TikTok and Google Business Profile",
      "24-month worldwide licence, every channel",
      "First cut in 5 working days, full delivery in 10",
      "Two revision rounds included",
      "For a single hotel or vacation rental that needs direct-booking ammunition",
    ],
    badge: null,
  },
  {
    key: "marketing-suite",
    name: "Marketing Suite",
    line: "The hero film, plus every room type and social ratio you actually run.",
    features: [
      "Everything in Property Film. The hero film at the centre",
      "Dedicated room-type films: suites, bath, amenities, F&B",
      "8–10 social cutdowns across ratios and channels",
      "A 6-second bumper for paid campaigns",
      "For boutique hotels ready to lift their full digital presence",
    ],
    badge: "Most chosen",
  },
  {
    key: "brand-film",
    name: "Brand Film",
    line: "A signature film graded to your brand standard, the anchor of a season's campaign.",
    features: [
      "A 90-second signature brand film, campaign-ready",
      "Brand-standard grade, colour-managed to your identity",
      "Custom-quoted per project",
      "Add-ons: extra room-type films, subtitles and language versions, extra vertical packs, 72-hour rush turnaround, extended music licence, additional revision rounds, on-market photography add-on",
      "For five-star hotels, resorts and multi-property groups running seasonal campaigns",
    ],
    badge: null,
  },
];

export default function Pricing() {
  const { scrollTo } = useScroll();
  return (
    <section id="pricing" className="relative overflow-hidden px-6 py-20 sm:px-10 md:py-28 warm-radial" data-testid="pricing">
      <BigSectionNo n="07" />
      <div className="absolute inset-0" aria-hidden="true">
        <img
          src={images.pricing}
          alt=""
          loading="lazy"
          className="atmos h-full w-full object-cover opacity-[0.07]"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-ground via-ground/90 to-ground" />
      </div>
      <div className="relative z-10 mx-auto max-w-[1280px]">
        <Hairline className="mb-20" />
        <Reveal>
          <SectionNo n="07" label="Three ways to work" />
        </Reveal>
        <h2
          className="display mt-8 max-w-3xl text-primaryText"
          style={{
            fontSize: "clamp(2.25rem, 5vw, 4rem)",
            lineHeight: 1.02,
            letterSpacing: "-0.015em",
            fontWeight: 300,
          }}
        >
          <MaskReveal>The right engagement</MaskReveal>
          <MaskReveal delay={0.1}>for what you're running.</MaskReveal>
        </h2>
        <Reveal delay={0.16}>
          <p className="mt-8 max-w-xl text-base leading-relaxed text-mutedText">
            Three tiers, three stories. Every project is scoped and proposed
            in writing before a single frame moves. Send a note and we'll
            reply with the shape and the terms.
          </p>
        </Reveal>

        <Stagger className="mt-16 grid gap-6 lg:grid-cols-3">
          {TIERS.map((t) => (
            <StaggerItem key={t.key}>
              <div
                className="flex h-full flex-col rounded-[3px] border border-gold/25 bg-surface/70 p-8 backdrop-blur-[2px] glide hover:-translate-y-1.5 hover:border-gold/60 hover:shadow-[0_24px_70px_-24px_rgba(185,154,94,0.3)] sm:p-10"
                data-testid={`pricing-card-${t.key}`}
              >
                <div className="flex items-start justify-between">
                  <h3 className="eyebrow">{t.name}</h3>
                  {t.badge && (
                    <span className="rounded-full border border-gold/50 px-3 py-1 text-[10px] uppercase tracking-[0.18em] text-gold">
                      {t.badge}
                    </span>
                  )}
                </div>

                <p
                  className="display mt-6 text-primaryText"
                  style={{ fontSize: "clamp(1.5rem, 2.4vw, 2rem)", fontWeight: 300, lineHeight: 1.15 }}
                >
                  {t.line}
                </p>

                <ul className="mt-8 flex-1 space-y-3">
                  {t.features.map((f) => (
                    <li key={f} className="flex gap-3 text-sm text-mutedText">
                      <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-gold" />
                      <span className="leading-relaxed">{f}</span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => scrollTo("#enquiry")}
                  className="btn-sweep mt-10 w-full rounded-full border border-gold/60 py-4 text-sm tracking-wide text-primaryText transition-colors duration-300 hover:bg-gold hover:text-cream"
                  data-testid={`request-proposal-${t.key}`}
                >
                  Request a proposal
                </button>
              </div>
            </StaggerItem>
          ))}
        </Stagger>

        <Reveal delay={0.1}>
          <div
            className="mt-6 flex flex-col gap-6 rounded-[3px] border border-gold/25 bg-surface/50 p-8 backdrop-blur-[2px] glide hover:border-gold/50 sm:flex-row sm:items-center sm:justify-between sm:p-10"
            data-testid="pricing-groups"
          >
            <div className="max-w-2xl">
              <h3 className="eyebrow">Groups &amp; collections</h3>
              <p className="display mt-4 text-2xl text-primaryText sm:text-3xl" style={{ fontWeight: 300 }}>
                Properties proposed together. One visual language across the portfolio.
              </p>
              <p className="mt-3 text-sm leading-relaxed text-mutedText">
                For hotel groups and collections. Every address moving to the
                same standard, proposed on scope rather than a rate card.
              </p>
            </div>
            <button
              onClick={() => scrollTo("#enquiry")}
              className="btn-sweep w-full shrink-0 rounded-full border border-gold/60 px-8 py-4 text-sm tracking-wide text-primaryText transition-colors duration-300 hover:bg-gold hover:text-cream sm:w-auto"
              data-testid="request-proposal-groups"
            >
              Request a proposal
            </button>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
